import { Accessor, For, Setter, Show, Signal, Suspense, createContext, createEffect, createMemo, createResource, createSignal, on, useContext } from 'solid-js'
import { queryEditor as api } from 'st-app/api'
import style from './st-query-editor.module.css'
import STButton, { ButtonType } from '../st-button/st-button'
import { BOFieldType, BOPropertyInfo, ColumnAggregateType, QBAltCodeType, QBBreakType, QBDisplayType, STQuery, STQueryBO, STQueryBOProperty, STQueryConditionContainer } from 'st-app/api'
import { SetStoreFunction, createStore, produce } from 'solid-js/store'
import STInput from '../st-input/st-input'
import STTabGroup from '../st-tab-group/st-tab-group'
import STTab from '../st-tab/st-tab'
import { createProxySignal } from 'common'
import STCheckbox from '../st-checkbox/st-checkbox'
import Select from '../st-select/st-select'
import STTable from '../st-table/st-table'

export class STQueryEditorContext {
    query: Accessor<string>
    setQuery: Setter<string>

    queryModel: Partial<STQuery>
    setQueryModel: SetStoreFunction<Partial<STQuery>>

    selectedBoAlias: Accessor<string>
    setSelectedBoAlias: Setter<string>

    isLoading: Accessor<boolean>
    setIsLoading: Setter<boolean>

    selectedBo: Accessor<STQueryBO | undefined>

    allBos: Accessor<STQueryBO[]>
    properties: Accessor<STQueryBOProperty[]>

    boProperties: Record<string, BOPropertyInfo[]> = {}

    constructor(query?: Signal<string>) {
        [this.query, this.setQuery] = query ?? createSignal('');
        [this.selectedBoAlias, this.setSelectedBoAlias] = createSignal('');
        [this.queryModel, this.setQueryModel] = createStore<Partial<STQuery>>({
            conditionContainer: new STQueryConditionContainer(),
            sorts: [],
            primaryBO: undefined,
            properties: [],
        });
        [this.isLoading, this.setIsLoading] = createSignal(false);

        this.selectedBo = createMemo(() => {
            let bo: STQueryBO | undefined = this.queryModel.primaryBO ? { ...this.queryModel.primaryBO } : undefined;
            if (!bo) return undefined;

            const findBo = (bo: STQueryBO): STQueryBO | undefined => {
                if (bo.alias === this.selectedBoAlias()) return bo;

                for (let child of bo.children) {
                    const found = findBo(child);
                    if (found) return found;
                }

                return undefined;
            }

            return findBo(bo);
        })

        this.allBos = createMemo(() => {
            const bos: STQueryBO[] = [];

            const addBo = (bo: STQueryBO) => {
                bos.push(bo);

                for (let child of bo.children) {
                    addBo(child);
                }
            }

            if (this.queryModel.primaryBO) {
                addBo(this.queryModel.primaryBO);
            }

            return bos;
        });

        this.properties = createMemo(() => {
            if (!this.selectedBo()) return [];
            
            const res: STQueryBOProperty[] = [];

            return res;
        })
    }

    removeBO(bo: STQueryBO) {
        console.log('removing', bo)
        if (!this.queryModel.primaryBO) return;
        if (this.queryModel.primaryBO.alias === bo.alias) {
            this.setQueryModel('primaryBO', undefined)
        }

        this.setQueryModel(produce(model => {
            const removeBoRecursive = (b: STQueryBO) => {
                const idx = b.children.findIndex((child: any) => child.alias === bo.alias);

                if (idx !== -1) {
                    console.log('splice', b.alias)
                    b.children.splice(idx, 1);
                    return;
                }

                for (let child of b.children) {
                    removeBoRecursive(child);
                }
            }
    
            removeBoRecursive(model.primaryBO!);
        }))
    }

    editProperty<T extends keyof STQueryBOProperty>(property: STQueryBOProperty, key: T, value: STQueryBOProperty[T]) {
        this.setQueryModel('properties', p => p.order === property.order, key, value);
    }
}

const Context = createContext<STQueryEditorContext>()

export class STQueryEditorProps {
    query?: Signal<string>
}

export default function STQueryEditor(props: STQueryEditorProps) {
    const [query, setQuery] = props.query ?? createSignal('')

    const editor = new STQueryEditorContext([query, setQuery])

    createEffect(on([() => props.query], async () => {
        editor.setIsLoading(true)
        try {
            const model = await api.ParseQuery(query())
            editor.setQueryModel(model)
        } catch (e) {
            console.error(e)
        }
        editor.setIsLoading(false)
    }))

    const [tabId, setTabId] = createSignal('columns')

    return (
        <Context.Provider value={editor}>
            <STTabGroup selectedTabId={[tabId, setTabId]}>
                <STTab id='columns' label="Columns">
                    <ColumnsTab />
                </STTab>

                <STTab id='conditions' label="Conditions">
                    <div>Conditions</div>
                </STTab>

                <STTab id='sorts' label="Sorts">
                    <div>Sorts</div>
                </STTab>

                <STTab id='type-in-query' label='Type in Query'>
                    <div>Type in Query</div>
                </STTab>
            </STTabGroup>
        </Context.Provider>
    )
}

STQueryEditor.defaultProps = new STQueryEditorProps();

const ColumnsTab = () => {
    const editor = useSTQueryEditor();
    return (
        <div class='flexbox' style='gap: 8px; padding-top: 8px;'>
            <div class={style['bo-panel']}>
                <Show when={!editor.isLoading()} fallback={<span>Loading...</span>}>
                    <ul class={style['bo-panel-left']}>
                        <Show when={editor.queryModel?.primaryBO} fallback={<AddRootBusinessObjectButton />}>
                            <BONode bo={editor.queryModel.primaryBO!} />
                        </Show>
                    </ul>

                    <div class={style['bo-panel-right']}>
                        <PropertyPanel />
                    </div>
                </Show>
            </div>

            <div>
                <STTable>
                    <STTable.Head>
                        <STTable.HeadCell>Actions</STTable.HeadCell>
                        <STTable.HeadCell>Actions</STTable.HeadCell>
                        <STTable.HeadCell>Actions</STTable.HeadCell>
                        <STTable.HeadCell>Actions</STTable.HeadCell>
                    </STTable.Head>
                </STTable>
            </div>

            <div>
                <table class={style['property-table']}>
                    <thead>
                        <tr>
                            <th rowspan="2"></th>
                            <th rowspan="2"></th>
                            <th rowspan="2"></th>
                            <th rowspan="2">Name</th>
                            <th rowspan="2">Label Override</th>
                            <th rowspan="2">Width</th>
                            <th rowspan="2">Hide</th>
                            <th colspan="2">Lookup Display</th>
                            <th rowspan="2">Group</th>
                            <th rowspan="2">Aggregate</th>
                            <th rowspan="2">Break</th>
                        </tr>
                        <tr>
                            <th>Display Type</th>
                            <th>Include Alt Code</th>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={editor.queryModel.properties}>{property => (
                            <tr>
                                <td>
                                    <STButton onClick={() => {}} type="info">
                                        <span class='fa fa-trash' style='color: var(--st-color-red)'></span>
                                    </STButton>
                                </td>

                                <td>
                                    <STButton onClick={() => {}} type="info">
                                        <span class='fa fa-bars' style='cursor: move;'></span>
                                    </STButton>
                                </td>

                                <td>
                                    <STButton onClick={() => {}} type="default">
                                        <span class='fa fa-chart-simple'></span>
                                    </STButton>
                                </td>

                                <td>
                                    <span>{property.name.srcElement}</span>
                                </td>

                                <td>
                                    <STInput value={createProxySignal(() => property.labelOverride ?? '', (v) => editor.editProperty(property, 'labelOverride', v))} />
                                </td>

                                <td>
                                    <STInput value={createProxySignal(() => property.width ?? '', (v) => editor.editProperty(property, 'width', v))} />
                                </td>
                                
                                <td>
                                    <STCheckbox value={createProxySignal(() => property.hide ?? false, (v) => editor.editProperty(property, 'hide', v))} label=""/>
                                </td>

                                <td>
                                    <Select label='' value={createProxySignal(() => QBDisplayType[property.displayType], (v) => editor.editProperty(property, 'displayType', v as unknown as QBDisplayType))}>
                                        <Select.Option<QBDisplayType> value={QBDisplayType.Default}>Default</Select.Option>
                                        <Select.Option<QBDisplayType> value={QBDisplayType.Code}>Code</Select.Option>
                                        <Select.Option<QBDisplayType> value={QBDisplayType.Description}>Description</Select.Option>
                                        <Select.Option<QBDisplayType> value={QBDisplayType.Both}>Both</Select.Option>
                                    </Select>
                                </td>

                                <td>
                                    <Select label='' value={createProxySignal(() => QBAltCodeType[property.altCodeType] ?? '', (v) => editor.editProperty(property, 'altCodeType', v as unknown as QBAltCodeType))}>
                                        <Select.Option<QBAltCodeType> value={QBAltCodeType.None}>None</Select.Option>
                                        <Select.Option<QBAltCodeType> value={QBAltCodeType.SIS}>SIS</Select.Option>
                                        <Select.Option<QBAltCodeType> value={QBAltCodeType.State}>State</Select.Option>
                                        <Select.Option<QBAltCodeType> value={QBAltCodeType.Alt3}>Alt3</Select.Option>
                                        <Select.Option<QBAltCodeType> value={QBAltCodeType.SIF}>SIF</Select.Option>
                                        <Select.Option<QBAltCodeType> value={QBAltCodeType.EdFi}>EdFi</Select.Option>
                                        <Select.Option<QBAltCodeType> value={QBAltCodeType.Federal}>Federal</Select.Option>
                                    </Select>
                                </td>

                                <td>
                                    <STInput value={createProxySignal(() => property.group ?? '', (v) => editor.editProperty(property, 'group', v))} />
                                </td>

                                <td>
                                    <Select label='' value={createProxySignal(() => ColumnAggregateType[property.aggregateType], v => editor.editProperty(property, 'aggregateType', v as unknown as ColumnAggregateType))}>
                                        <Select.Option<ColumnAggregateType> value={ColumnAggregateType.None}>None</Select.Option>
                                        <Select.Option<ColumnAggregateType> value={ColumnAggregateType.Sum}>Sum</Select.Option>
                                        <Select.Option<ColumnAggregateType> value={ColumnAggregateType.Count}>Count</Select.Option>
                                        <Select.Option<ColumnAggregateType> value={ColumnAggregateType.Min}>Min</Select.Option>
                                        <Select.Option<ColumnAggregateType> value={ColumnAggregateType.Max}>Max</Select.Option>
                                        <Select.Option<ColumnAggregateType> value={ColumnAggregateType.Avg}>Avg</Select.Option>
                                    </Select>
                                </td>

                                <td>
                                    <Select label='' value={createProxySignal(() => QBBreakType[property.break], v => editor.editProperty(property, 'break', v as unknown as QBBreakType))}>
                                        <Select.Option<QBBreakType> value={QBBreakType.None}>None</Select.Option>
                                        <Select.Option<QBBreakType> value={QBBreakType.Group}>Group</Select.Option>
                                        <Select.Option<QBBreakType> value={QBBreakType.Page}>Page</Select.Option>
                                    </Select>
                                </td>
                            </tr>
                        )}</For>
                    </tbody>
                </table>
            </div>
        </div>
    )
}

const BONode = (props: { bo: STQueryBO }) => {
    const editor = useSTQueryEditor();

    const [isOpen, setIsOpen] = createSignal(true);

    return (
        <li class={`flexbox ${style['bo-node']}`}>
            <div class={style['bo-node-label']} data-selected={props.bo.alias === editor.selectedBoAlias()} onClick={() => editor.setSelectedBoAlias(props.bo.alias ?? '')}>
                <div class='flexbox horizontal' style='gap: 4px;'>
                    <span
                        class='fa-regular'
                        classList={{
                            ['fa-square-minus']: isOpen(),
                            ['fa-square-plus']: !isOpen(),
                        }}
                        onClick={() => setIsOpen(!isOpen())}
                        style={{
                            opacity: props.bo.children.length > 0 ? 1 : 0,
                        }}
                    ></span>

                    <span>{`${props.bo.name} (${props.bo.alias})`}</span>
                </div>

                <div class={style['bo-node-actions']}>
                    <STButton onClick={() => {}} type="primary">
                        <span class='fa fa-plus'></span>
                    </STButton>

                    <STButton onClick={() => { editor.removeBO(props.bo) }} type="danger">
                        <span class='fa fa-trash'></span>
                    </STButton>
                </div>
            </div>
            <ul class={style['bo-node-list']}>
                <Show when={isOpen()}>
                    <For each={props.bo.children}>{childBo => (
                        <BONode bo={childBo} />
                    )}</For>
                </Show>
            </ul>
        </li>
    )
}

const AddRootBusinessObjectButton = () => {
    return (
        <STButton type="primary" onClick={() => {}}>
            <div class='flexbox'>
                <span class='fa fa-plus' style='font-size: 3rem;'></span>
                <span style='font-size: 0.9rem;'>Add Root Business Object</span>
            </div>
        </STButton>
    )
}

const PropertyPanel = () => {
    const [searchValue, setSearchValue] = createSignal('')
    const editor = useSTQueryEditor();

    const [isLoading, setIsLoading] = createSignal(false);
    const [properties, setProperties] = createSignal<BOPropertyInfo[]>([]);

    createEffect(on([() => editor.selectedBo()], async () => {
        console.log('loading properties')
        const selectedBo = editor.selectedBo();
        if (!selectedBo || !selectedBo.BOID) return;

        const cached = editor.boProperties[selectedBo.BOID]
        
        if (cached) {
            setProperties(Object.values(cached));
            return;
        }

        setIsLoading(true);

        const properties = await api.LoadProperties(selectedBo.BOID, true);
        editor.boProperties[selectedBo.BOID] = properties;
        setProperties(properties);

        setIsLoading(false);
    }))

    const filteredProperties = createMemo(() => {
        return properties().filter(p => p.srcElement?.toLowerCase().includes(searchValue().toLowerCase()))
    })


    return (
        <div class={style['property-panel']}>
            <STInput placeholder='Filter' icon='fa fa-search' value={[searchValue, setSearchValue]}/>
            <div class={style['property-list']}>
                <span class={style['property-helper-text']}>Select a property to add it to the list</span>
                <For each={filteredProperties()}>{(property) => {
                    const searchIdx = createMemo(() => {
                        return (searchValue() && property.srcElement) ? property.srcElement.toLowerCase().indexOf(searchValue().toLowerCase()) : -1;
                    });

                    return (
                        <div class={style['property-list-item']} data-included={editor.properties().some(p => p.name.srcElement === property.srcElement)}>
                            <div style='grid-column: 1;'>
                                <Show when={searchIdx() > -1} fallback={property.srcElement}>
                                    <span>{property.srcElement?.substring(0, searchIdx())}</span>
                                    <span style='color: red;'>{property.srcElement?.substring(searchIdx(), searchIdx() + searchValue().length)}</span>
                                    <span>{property.srcElement?.substring(searchIdx() + searchValue().length)}</span>
                                </Show>
                            </div>

                            <div style='grid-column: 2;'>
                                {property.type}
                            </div>
                        </div>
                    )
                }}</For>
            </div>
        </div>
    )
}

export const useSTQueryEditor = () => {
    const ctx = useContext(Context);
    if (!ctx) {
        throw new Error('useSTQueryEditor must be used within a STQueryEditorContext.Provider')
    }

    return ctx;
}